import { HeadquarterStats } from './types';

/**
 * Get usage rate CSS class based on percentage
 */
export function getUsageRateClass(rate: number): string {
  if (rate >= 70) return 'rate-high';
  if (rate >= 50) return 'rate-medium';
  return 'rate-low';
}

/**
 * Format number with comma separators
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('ja-JP');
}

/**
 * Format date range for display
 */
export function formatDateRange(startDate: Date, endDate: Date): string {
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  return `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
}

/**
 * Get tier usage rate CSS class
 */
export function getTierRateClass(rate: number): string {
  if (rate >= 30) return 'rate-high';
  if (rate >= 10) return 'rate-medium';
  return 'rate-low';
}

/**
 * Generate analysis comment for a headquarter
 */
export function generateHeadquarterComment(stat: HeadquarterStats): string {
  const parts: string[] = [];

  // Usage rate comment
  const targetRate = 97;
  const rateGap = targetRate - stat.usageRate;
  parts.push(`利用率${stat.usageRate.toFixed(1)}%。業界先進企業（${targetRate}%）まであと${rateGap.toFixed(1)}pt。`);

  // Cost tier ¥500+ comment
  const costTierTarget = 50;
  const costTierGap = costTierTarget - stat.costTier500Rate;
  if (stat.costTier500Rate < costTierTarget) {
    parts.push(`¥500以上利用率${stat.costTier500Rate.toFixed(0)}%。目標${costTierTarget}%まであと${costTierGap.toFixed(0)}ptの改善が必要です。`);
  } else {
    parts.push(`¥500以上利用率${stat.costTier500Rate.toFixed(0)}%。目標の${costTierTarget}%を達成。`);
  }

  // Inactive users comment
  if (stat.inactiveUsers > 0) {
    parts.push(`未利用者${stat.inactiveUsers}名について：IT業界では生成AI活用が標準化しており、情報通信業界の活用率は65.6%、先進企業では97%に達しています。使わないことは現状維持ではなく、日々競争力を失っている状態です。未利用者が業務効率化に非協力的な姿勢を示しているのであれば、業務命令として活用を促すことを検討してください。`);
  }

  return parts.join(' ');
}

/**
 * Mask email address for privacy (server-side)
 * e.g., "john.doe@example.com" → "***.do*@example.com"
 */
export function maskEmail(email: string): string {
  if (!email || typeof email !== 'string') return email;
  const atIndex = email.indexOf('@');
  if (atIndex === -1) return email;

  const localPart = email.substring(0, atIndex);
  const domainPart = email.substring(atIndex);

  const lastDotIndex = localPart.lastIndexOf('.');
  if (lastDotIndex === -1) {
    if (localPart.length <= 1) return '***' + domainPart;
    return localPart.replace(/./g, '*') + domainPart;
  }

  const lastSegment = localPart.substring(lastDotIndex + 1);
  if (lastSegment.length <= 1) {
    return '***.*' + domainPart;
  }
  const visiblePart = lastSegment.substring(0, lastSegment.length - 1);
  return '***.' + visiblePart + '*' + domainPart;
}
